'use client';

import { IT_INT } from '@slims/shared/constants';
import { CheckCircle2, Loader2, Receipt, XCircle } from 'lucide-react';
import { useState } from 'react';
import { ConfirmationDialog } from '../confirmation-dialog/confirmation-dialog';

export interface MockBollettaMeterResult {
	meterId: string;
	codice: string;
	written: number;
	error?: string;
}

export interface MockBolletteCardProps {
	busy: boolean;
	/** Misuratori della CER selezionata, o null finché non è noto. */
	meterCount: number | null;
	/** Avanzamento durante la generazione: misuratori già fatti / totale. */
	progress: { done: number; total: number } | null;
	/** Esito dell'ultima generazione, un misuratore per riga. */
	results: MockBollettaMeterResult[] | null;
	/** Avvia la generazione per tutti i misuratori della CER selezionata. */
	onGenerate: () => void;
}

/**
 * Genera bollette fittizie (livello 2, provenienza bolletta) per tutti i
 * misuratori della CER selezionata — serve a verificare la catena
 * bolletta → reale → stimato senza aspettare una bolletta vera. A differenza
 * degli strumenti GSE della pagina, funziona anche fuori dalla modalità demo:
 * la generazione non sovrascrive mai una misura reale già presente (vince
 * sempre il contatore) e resta identificabile — quindi rimovibile — per
 * provenienza.
 */
export function MockBolletteCard({
	busy,
	meterCount,
	progress,
	results,
	onGenerate,
}: MockBolletteCardProps) {
	const [confirmOpen, setConfirmOpen] = useState(false);
	const totalWritten = results?.reduce((s, r) => s + r.written, 0) ?? null;
	const failed = results?.filter((r) => r.error).length ?? 0;

	return (
		<div className="card">
			<div className="card-head">
				<span className="card-title">
					<Receipt className="mr-1 inline h-3.5 w-3.5 text-warn" />
					Bollette fittizie
				</span>
				<span className="card-badge badge-orange">TEST LIVELLO 3</span>
			</div>

			<p className="mb-2 text-[12px] text-text-muted">
				Genera un anno di bollette sintetiche (livello Reale, provenienza bolletta) per ogni
				misuratore della CER selezionata, poi ricalcola il livello Stimato su quell'ancora - così si
				può verificare la catena bolletta → reale → stimato su un socio, senza aspettare una
				bolletta vera.
			</p>
			<p className="mb-3 text-[11px] italic text-text-muted">
				Non sovrascrive mai una misura reale del contatore già presente: riempie solo i giorni che
				oggi non hanno una misura. Funziona anche su una CER reale - resta identificabile e
				rimovibile per provenienza.
			</p>

			<div className="flex flex-wrap items-center gap-2">
				<button
					type="button"
					onClick={() => setConfirmOpen(true)}
					disabled={busy || !meterCount}
					className="inline-flex items-center gap-1.5 rounded border border-warn/50 bg-warn/10 px-3 py-1 text-[13px] text-warn transition-colors hover:bg-warn/20 disabled:opacity-50"
				>
					{busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Receipt className="h-3 w-3" />}
					{busy
						? progress
							? `Generazione… ${progress.done}/${progress.total}`
							: 'Generazione…'
						: `Genera bollette fittizie${meterCount ? ` (${meterCount} misuratori)` : ''}`}
				</button>
				{!busy && meterCount === 0 && (
					<span className="text-[12px] text-text-muted">
						Nessun misuratore in questa configurazione
					</span>
				)}
			</div>

			{results && (
				<div className="mt-3 space-y-1">
					{totalWritten != null && (
						<p className="text-[12px] font-medium text-text-primary">
							{totalWritten.toLocaleString('it-IT', IT_INT)} slot scritti su {results.length}{' '}
							misuratori
							{failed > 0 ? ` · ${failed} falliti` : ''}
						</p>
					)}
					<ul className="max-h-40 space-y-0.5 overflow-y-auto">
						{results.map((r) => (
							<li key={r.meterId} className="flex items-start gap-1.5 text-[12px] text-text-muted">
								{r.error ? (
									<XCircle className="mt-0.5 h-3 w-3 shrink-0 text-accent-red" />
								) : (
									<CheckCircle2 className="mt-0.5 h-3 w-3 shrink-0 text-accent-green" />
								)}
								<span className="min-w-0 break-all">
									<span className="text-text-primary">{r.codice}</span>
									{r.error ? ` - ${r.error}` : ` · ${r.written} slot`}
								</span>
							</li>
						))}
					</ul>
				</div>
			)}

			<ConfirmationDialog
				open={confirmOpen}
				onOpenChange={setConfirmOpen}
				title="Generare bollette fittizie per tutta la CER?"
				description={`Scrive dati di bolletta sintetici nel livello Reale di ${meterCount ?? 0} misuratori (solo dove manca già una misura) e ricalcola il livello Stimato. Pensato per verificare la catena bolletta → stimato, anche su una configurazione reale.`}
				confirmText="Sì, genera"
				cancelText="Annulla"
				variant="default"
				onConfirm={() => {
					onGenerate();
				}}
			/>
		</div>
	);
}
